import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Nav from "@/components/Nav";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Users, Database, FlagTriangleRight, ListChecks, ShieldCheck, Infinity as InfinityIcon, EyeOff, Mail, History, CheckCircle2, EyeOff as EyeOffBulk, Undo2 } from "lucide-react";
import { toast } from "sonner";

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <div className={`rounded-xl border border-border bg-[#121214] p-5 ${accent ? "tracing-border" : ""}`} data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center justify-between">
        <div className="text-xs text-muted-foreground uppercase tracking-[0.18em]">{label}</div>
        <Icon className="w-4 h-4 text-primary" />
      </div>
      <div className="mt-2 text-3xl font-medium tracking-tight">{value}</div>
    </div>
  );
}

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [lists, setLists] = useState([]);
  const [reported, setReported] = useState([]);
  const [audit, setAudit] = useState([]);
  const [search, setSearch] = useState("");
  const [credEdit, setCredEdit] = useState({});
  const [sendingDigest, setSendingDigest] = useState(false);
  const [selectedLeads, setSelectedLeads] = useState({});
  const [bulkBusy, setBulkBusy] = useState(false);
  const { impersonate } = useAuth();
  const nav = useNavigate();

  const load = async () => {
    try {
      const [s, u, l, r, a] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/users"),
        api.get("/admin/lead-lists"),
        api.get("/admin/reported-leads"),
        api.get("/admin/audit-logs"),
      ]);
      setStats(s.data);
      setUsers(u.data);
      setLists(l.data);
      setReported(r.data);
      setAudit(a.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load");
    }
  };

  useEffect(() => { load(); }, []);

  const updateUser = async (id, patch) => {
    try {
      await api.patch(`/admin/users/${id}`, patch);
      toast.success("User updated");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Update failed");
    }
  };

  const impersonateUser = async (userId, email) => {
    const r = await impersonate(userId);
    if (r.ok) {
      toast.success(`Now viewing as ${email}`);
      nav("/dashboard");
    } else {
      toast.error(r.error);
    }
  };

  const sendDigestNow = async () => {
    setSendingDigest(true);
    try {
      await api.post("/admin/digest/send-now");
      toast.success("Digest send started — check email soon");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Send failed");
    } finally {
      setSendingDigest(false);
    }
  };

  const bulkModerate = async (action) => {
    const ids = Object.keys(selectedLeads).filter((k) => selectedLeads[k]);
    if (ids.length === 0) {
      toast.error("Select at least one lead");
      return;
    }
    setBulkBusy(true);
    try {
      const { data } = await api.patch("/admin/leads/bulk", { ids, action });
      toast.success(`${data.modified} lead(s) ${action === "verify" ? "marked verified" : action === "hide" ? "hidden" : "restored"}`);
      setSelectedLeads({});
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Bulk action failed");
    } finally {
      setBulkBusy(false);
    }
  };

  const toggleLead = (id) => setSelectedLeads((p) => ({ ...p, [id]: !p[id] }));
  const toggleAllLeads = () => {
    const allSelected = reported.length > 0 && reported.every((l) => selectedLeads[l.id]);
    if (allSelected) setSelectedLeads({});
    else setSelectedLeads(Object.fromEntries(reported.map((l) => [l.id, true])));
  };
  const selectedCount = Object.values(selectedLeads).filter(Boolean).length;

  const filteredUsers = users.filter((u) => u.email.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="min-h-screen">
      <Nav />
      <main className="max-w-7xl mx-auto px-6 py-10">
        <div className="flex items-end justify-between gap-4 mb-8">
          <div>
            <div className="overline text-primary flex items-center gap-2">
              <ShieldCheck className="w-3.5 h-3.5" /> Admin
            </div>
            <h1 className="mt-2 text-4xl font-medium tracking-tighter">Platform console</h1>
            <p className="text-sm text-muted-foreground mt-2">Users, lead lists, and reported leads at a glance.</p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              className="rounded-full border-primary/40 text-primary hover:bg-primary/10"
              onClick={sendDigestNow}
              disabled={sendingDigest}
              data-testid="send-digest-now-btn"
            >
              <Mail className="w-4 h-4 mr-1.5" /> {sendingDigest ? "Sending…" : "Send digest"}
            </Button>
            <Button
              variant="outline"
              className="rounded-full border-primary/40 text-primary hover:bg-primary/10"
              onClick={async () => {
                try {
                  await api.post("/admin/moderation-review/send-now");
                  toast.success("Moderation review email sent");
                } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Send failed"); }
              }}
              data-testid="send-mod-review-btn"
            >
              <FlagTriangleRight className="w-4 h-4 mr-1.5" /> Send mod review
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          <StatCard icon={Users} label="Users" value={stats?.users_total ?? "—"} accent />
          <StatCard icon={Database} label="Lead lists" value={stats?.lists_total ?? "—"} />
          <StatCard icon={ListChecks} label="Leads" value={stats?.leads_total ?? "—"} />
          <StatCard icon={FlagTriangleRight} label="Reported" value={stats?.reported_total ?? "—"} />
        </div>

        <Tabs defaultValue="users" data-testid="admin-tabs">
          <TabsList>
            <TabsTrigger value="users" data-testid="tab-users">Users</TabsTrigger>
            <TabsTrigger value="lists" data-testid="tab-lists">Lead lists</TabsTrigger>
            <TabsTrigger value="reported" data-testid="tab-reported">Reported leads</TabsTrigger>
            <TabsTrigger value="audit" data-testid="tab-audit">Audit log</TabsTrigger>
          </TabsList>

          {/* USERS */}
          <TabsContent value="users" className="mt-6">
            <div className="flex items-center justify-between gap-4 mb-4">
              <Input
                placeholder="Search by email…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="max-w-sm"
                data-testid="admin-user-search"
              />
              <div className="text-xs text-muted-foreground">{filteredUsers.length} of {users.length}</div>
            </div>
            <div className="rounded-xl border border-border bg-[#121214] overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Credits</TableHead>
                    <TableHead>Plan</TableHead>
                    <TableHead>Lists</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredUsers.length === 0 ? (
                    <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">No users found.</TableCell></TableRow>
                  ) : filteredUsers.map((u) => (
                    <TableRow key={u.id} data-testid={`admin-user-row-${u.id}`}>
                      <TableCell className="font-medium">{u.email}</TableCell>
                      <TableCell>
                        <span className={`text-xs px-2 py-0.5 rounded border ${u.role === "admin" ? "bg-primary/10 text-primary border-primary/20" : "border-border text-muted-foreground"}`}>
                          {u.role}
                        </span>
                      </TableCell>
                      <TableCell className="mono">
                        {u.credits === -1 ? (
                          <span className="inline-flex items-center gap-1 text-primary"><InfinityIcon className="w-3.5 h-3.5" /> Unlimited</span>
                        ) : u.credits}
                      </TableCell>
                      <TableCell className="capitalize text-sm text-muted-foreground">{u.plan}</TableCell>
                      <TableCell className="mono">{u.lists_count}</TableCell>
                      <TableCell className="text-right space-x-2">
                        <Input
                          type="number"
                          value={credEdit[u.id] ?? ""}
                          placeholder={u.credits === -1 ? "∞" : String(u.credits)}
                          onChange={(e) => setCredEdit((p) => ({ ...p, [u.id]: e.target.value }))}
                          className="inline-flex w-24 h-8"
                          data-testid={`admin-credits-input-${u.id}`}
                        />
                        <Button
                          size="sm"
                          variant="outline"
                          className="rounded-full"
                          onClick={() => {
                            const v = credEdit[u.id];
                            if (v === undefined || v === "") return;
                            updateUser(u.id, { credits: parseInt(v, 10) });
                            setCredEdit((p) => ({ ...p, [u.id]: "" }));
                          }}
                          data-testid={`admin-set-credits-${u.id}`}
                        >Set</Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="rounded-full"
                          onClick={() => updateUser(u.id, { credits: -1 })}
                          data-testid={`admin-grant-unlimited-${u.id}`}
                        >∞</Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="rounded-full"
                          onClick={() => impersonateUser(u.id, u.email)}
                          disabled={u.role === "admin"}
                          data-testid={`admin-impersonate-${u.id}`}
                        ><EyeOff className="w-3.5 h-3.5 mr-1" /> View as</Button>
                        <Button
                          size="sm"
                          className="rounded-full bg-primary hover:bg-[#FF7043] text-white"
                          onClick={() => updateUser(u.id, { role: u.role === "admin" ? "user" : "admin", credits: u.role === "admin" ? 1 : -1 })}
                          data-testid={`admin-toggle-role-${u.id}`}
                        >{u.role === "admin" ? "Revoke admin" : "Make admin"}</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* LISTS */}
          <TabsContent value="lists" className="mt-6">
            <div className="rounded-xl border border-border bg-[#121214] overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead>Business</TableHead>
                    <TableHead>User</TableHead>
                    <TableHead>Industry</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Leads</TableHead>
                    <TableHead>Created</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {lists.length === 0 ? (
                    <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">No lead lists yet.</TableCell></TableRow>
                  ) : lists.map((l) => (
                    <TableRow key={l.id} data-testid={`admin-list-row-${l.id}`}>
                      <TableCell className="font-medium">{l.business_name}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{l.user_email}</TableCell>
                      <TableCell className="text-sm">{l.industry}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{l.location}</TableCell>
                      <TableCell>
                        <span className={`text-xs px-2 py-0.5 rounded border ${
                          l.status === "ready" ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20"
                          : l.status === "failed" ? "bg-red-500/10 text-red-400 border-red-500/20"
                          : "bg-amber-500/10 text-amber-400 border-amber-500/20"
                        }`}>{l.status}</span>
                      </TableCell>
                      <TableCell className="mono">{l.leads_count}/{l.count}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{new Date(l.created_at).toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* REPORTED */}
          <TabsContent value="reported" className="mt-6">
            {/* Bulk action bar */}
            <div className="flex items-center justify-between gap-4 mb-4" data-testid="bulk-action-bar">
              <div className="text-sm text-muted-foreground">
                {selectedCount > 0 ? (
                  <>Selected <span className="text-foreground font-medium" data-testid="bulk-selected-count">{selectedCount}</span> of {reported.length}</>
                ) : (
                  <>{reported.length} flagged lead{reported.length === 1 ? "" : "s"}</>
                )}
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  className="rounded-full border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"
                  disabled={selectedCount === 0 || bulkBusy}
                  onClick={() => bulkModerate("verify")}
                  data-testid="bulk-verify-btn"
                ><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Mark verified</Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="rounded-full border-red-500/30 text-red-400 hover:bg-red-500/10"
                  disabled={selectedCount === 0 || bulkBusy}
                  onClick={() => bulkModerate("hide")}
                  data-testid="bulk-hide-btn"
                ><EyeOffBulk className="w-3.5 h-3.5 mr-1" /> Hide</Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="rounded-full"
                  disabled={selectedCount === 0 || bulkBusy}
                  onClick={() => bulkModerate("unhide")}
                  data-testid="bulk-unhide-btn"
                ><Undo2 className="w-3.5 h-3.5 mr-1" /> Restore</Button>
              </div>
            </div>

            <div className="rounded-xl border border-border bg-[#121214] overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="w-10">
                      <input
                        type="checkbox"
                        className="accent-primary"
                        checked={reported.length > 0 && reported.every((l) => selectedLeads[l.id])}
                        onChange={toggleAllLeads}
                        data-testid="bulk-select-all"
                      />
                    </TableHead>
                    <TableHead>Company</TableHead>
                    <TableHead>Reported by</TableHead>
                    <TableHead>Fit</TableHead>
                    <TableHead>Moderation</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead className="text-right">Open</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reported.length === 0 ? (
                    <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">No reported leads.</TableCell></TableRow>
                  ) : reported.map((l) => (
                    <TableRow key={l.id} data-testid={`admin-reported-row-${l.id}`} className={selectedLeads[l.id] ? "bg-primary/5" : ""}>
                      <TableCell>
                        <input
                          type="checkbox"
                          className="accent-primary"
                          checked={!!selectedLeads[l.id]}
                          onChange={() => toggleLead(l.id)}
                          data-testid={`bulk-select-${l.id}`}
                        />
                      </TableCell>
                      <TableCell className="font-medium">{l.company}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{l.user_email}</TableCell>
                      <TableCell className="mono">{l.fit_score}</TableCell>
                      <TableCell>
                        <span className={`text-xs px-2 py-0.5 rounded border ${
                          l.moderation_status === "verified" ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20"
                          : l.moderation_status === "hidden" ? "bg-red-500/10 text-red-400 border-red-500/20"
                          : "bg-amber-500/10 text-amber-400 border-amber-500/20"
                        }`}>{l.moderation_status || "pending"}</span>
                      </TableCell>
                      <TableCell>
                        {l.source_url ? (
                          <a href={l.source_url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline break-all">{l.source_url}</a>
                        ) : <span className="text-xs text-muted-foreground">—</span>}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button asChild size="sm" variant="outline" className="rounded-full">
                          <Link to={`/leads/${l.id}`}>Open</Link>
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* AUDIT */}
          <TabsContent value="audit" className="mt-6">
            <div className="rounded-xl border border-border bg-[#121214] overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead>When</TableHead>
                    <TableHead>Admin</TableHead>
                    <TableHead>Action</TableHead>
                    <TableHead>Target</TableHead>
                    <TableHead>Details</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {audit.length === 0 ? (
                    <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">No admin actions yet.</TableCell></TableRow>
                  ) : audit.map((a) => (
                    <TableRow key={a.id} data-testid={`admin-audit-row-${a.id}`}>
                      <TableCell className="text-xs text-muted-foreground mono">{new Date(a.created_at).toLocaleString()}</TableCell>
                      <TableCell className="text-sm">{a.admin_email}</TableCell>
                      <TableCell>
                        <span className="text-xs px-2 py-0.5 rounded border border-primary/20 bg-primary/5 text-primary inline-flex items-center gap-1">
                          <History className="w-3 h-3" /> {a.action}
                        </span>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">{a.target_user_email || "—"}</TableCell>
                      <TableCell className="text-sm">{a.details || "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}